import React, { Component, ErrorInfo, ReactNode } from "react";
import { RefreshCw, Copy, Check, ShieldAlert, AlertTriangle, WifiOff, Clock } from "lucide-react";

export interface StreamErrorBoundaryProps {
  children: ReactNode;
  fallbackText?: string;
  onResetStream?: () => void;
  onRetry?: () => void;
  fallback?: ReactNode | ((error: Error, reset: () => void) => ReactNode);
}

export interface StreamErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  copied: boolean;
  errorType: 'network' | 'timeout' | 'interruption' | 'render';
}

/**
 * StreamErrorBoundary Component
 * Catches stream interruptions, network drops, Worker timeouts, or rendering exceptions
 * without crashing the surrounding page UI. Preserves partial accumulated text.
 */
export class StreamErrorBoundary extends Component<StreamErrorBoundaryProps, StreamErrorBoundaryState> {
  constructor(props: StreamErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      copied: false,
      errorType: 'interruption',
    };
    this.handleCopyFallback = this.handleCopyFallback.bind(this);
    this.handleReset = this.handleReset.bind(this);
    this.handleRetry = this.handleRetry.bind(this);
  }

  static getDerivedStateFromError(error: Error): Partial<StreamErrorBoundaryState> {
    const msg = error?.message?.toLowerCase() || '';
    let errorType: 'network' | 'timeout' | 'interruption' | 'render' = 'render';

    if (msg.includes('network') || msg.includes('fetch') || msg.includes('offline') || msg.includes('failed to fetch')) {
      errorType = 'network';
    } else if (msg.includes('timeout') || msg.includes('timed out') || msg.includes('abort')) {
      errorType = 'timeout';
    } else if (msg.includes('stream') || msg.includes('sse') || msg.includes('chunk')) {
      errorType = 'interruption';
    }

    return {
      hasError: true,
      error,
      copied: false,
      errorType,
    };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("[StreamErrorBoundary] Caught stream exception:", error, errorInfo);
  }

  handleCopyFallback() {
    if (this.props.fallbackText) {
      navigator.clipboard.writeText(this.props.fallbackText);
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    }
  }

  handleReset() {
    this.setState({ hasError: false, error: null });
    if (this.props.onResetStream) {
      this.props.onResetStream();
    }
  }

  handleRetry() {
    this.setState({ hasError: false, error: null });
    if (this.props.onRetry) {
      this.props.onRetry();
    } else if (this.props.onResetStream) {
      this.props.onResetStream();
    }
  }

  override render() {
    if (this.state.hasError) {
      if (typeof this.props.fallback === 'function') {
        return this.props.fallback(this.state.error || new Error('Stream Error'), this.handleReset);
      } else if (this.props.fallback) {
        return this.props.fallback;
      }

      const getBadgeInfo = () => {
        switch (this.state.errorType) {
          case 'network':
            return {
              icon: <WifiOff className="w-5 h-5 text-rose-400" />,
              title: 'Network Drop / Connectivity Loss',
              color: 'border-rose-500/40 bg-rose-950/30 text-rose-200'
            };
          case 'timeout':
            return {
              icon: <Clock className="w-5 h-5 text-amber-400" />,
              title: 'Cloudflare Worker Timeout / Abort',
              color: 'border-amber-500/40 bg-amber-950/30 text-amber-200'
            };
          case 'interruption':
            return {
              icon: <AlertTriangle className="w-5 h-5 text-amber-400" />,
              title: 'Stream Interruption / SSE Disconnect',
              color: 'border-amber-500/40 bg-amber-950/30 text-amber-200'
            };
          default:
            return {
              icon: <ShieldAlert className="w-5 h-5 text-amber-400" />,
              title: 'Stream Render Exception Intercepted',
              color: 'border-amber-500/40 bg-amber-950/30 text-amber-200'
            };
        }
      };

      const badge = getBadgeInfo();

      return (
        <div className={`p-6 my-4 border rounded-xl shadow-xl backdrop-blur-md transition-all ${badge.color}`}>
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-black/30 rounded-lg shrink-0">
              {badge.icon}
            </div>
            <div>
              <h4 className="font-bold text-base sm:text-lg">
                {badge.title}
              </h4>
              <p className="text-xs opacity-80">
                Localized error boundary active. Surrounding application remain stable.
              </p>
            </div>
          </div>

          <div className="p-3 my-3 bg-black/50 border border-white/10 rounded-lg text-xs font-mono opacity-90 overflow-x-auto">
            {this.state.error?.message || "An unexpected error occurred during stream processing."}
          </div>

          {this.props.fallbackText && (
            <div className="mt-4 mb-3">
              <label className="text-xs font-semibold uppercase tracking-wider font-mono opacity-80 mb-1 block">
                Preserved Accumulated Chunks:
              </label>
              <div className="p-3 bg-neutral-900/90 rounded-lg text-xs font-mono text-neutral-200 max-h-40 overflow-y-auto whitespace-pre-wrap border border-neutral-800">
                {this.props.fallbackText}
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3 mt-4 pt-3 border-t border-white/10">
            <button
              onClick={this.handleRetry}
              className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-neutral-950 font-bold text-xs rounded-lg transition-all shadow-md active:scale-95 cursor-pointer"
            >
              <RefreshCw className="w-4 h-4" /> Re-establish Stream & Retry
            </button>

            {this.props.fallbackText && (
              <button
                onClick={this.handleCopyFallback}
                className="inline-flex items-center gap-2 px-4 py-2 bg-neutral-800 hover:bg-neutral-700 text-neutral-200 font-medium text-xs rounded-lg border border-neutral-700 transition-all cursor-pointer"
              >
                {this.state.copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                {this.state.copied ? "Copied Partial Text!" : "Copy Partial Output"}
              </button>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export { StreamErrorBoundary as AIStreamErrorBoundary };
